'use client';

import {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
} from 'react';
import { FirebaseApp } from 'firebase/app';
import { firebaseService } from '../api/firebase-service';
import { logger } from '@/core/logger';

interface IFirebaseContext {
	app: FirebaseApp | null;
}

const FirebaseContext = createContext<IFirebaseContext | undefined>(undefined);

export interface FirebaseProviderProps {
	children: ReactNode;
}

export function FirebaseProvider({ children }: FirebaseProviderProps) {
	const [app, setApp] = useState<FirebaseApp | null>(null);

	useEffect(() => {
		let mounted = true;

		const initializeFirebase = async () => {
			try {
				const firebaseApp = await firebaseService.initialize();
				if (mounted) {
					setApp(firebaseApp);
				}
			} catch (err) {
				logger.error('Firebase initialization error', err);
			}
		};

		// Initialize Firebase after initial render
		const timeoutId = setTimeout(initializeFirebase, 100);

		return () => {
			clearTimeout(timeoutId);
			mounted = false;
		};
	}, []);

	const value: IFirebaseContext = {
		app,
	};

	return (
		<FirebaseContext.Provider value={value}>
			{children}
		</FirebaseContext.Provider>
	);
}

export function useFirebase(): IFirebaseContext {
	const context = useContext(FirebaseContext);

	if (!context) {
		throw new Error('useFirebase must be used within a FirebaseProvider');
	}

	return context;
}
